import { ChangeDetectorRef, Component, EventEmitter, Output, ViewEncapsulation } from '@angular/core';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { Observable, zip } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import { PackagesSearchBarBaseContainer } from '@packages/packages-search-bar/containers/packages-search-bar-base.container';
import { environment as ENV } from '@environment';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { FlightType } from '@search-bar/entities/flight-type';
import { SearchBarMobileService } from '@search-bar/services/search-bar-mobile.service';
import { AppFacade } from '@app/app.facade';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { GuestToPassengerUIPipe } from '@app/pipes/guest-to-paseenger.pipe';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { LocalStorageService } from '@app/services/local-storage.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { SwipeType } from '@ux/entities/swipe-types.enum';
import { AvoService } from '@avo/services/avo.service';
import { HeaderProductType } from '@header/entities/ds-header-config';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';

@Component({
  selector: 'app-packages-search-bar-mobile',
  templateUrl: './packages-search-bar-mobile.container.html',
  styleUrls: ['./packages-search-bar-mobile.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class PackagesSearchBarMobileContainer extends PackagesSearchBarBaseContainer {

  @Output() showMobileCalendarEvent: EventEmitter<CalendarField> = new EventEmitter<CalendarField>();

  public showFlightOptionSelection: boolean = false;
  public activeOption: string;
  public staticShowControls: boolean = false;
  public swipeStyle: SwipeType = SwipeType.FULL;
  public searchBy = SearchBy.PACKAGES;

  constructor(
    protected domManipulateService: DomManipulateService,
    protected appFacade: AppFacade,
    protected facade: BaseSearchBarFacade,
    protected searchBarMobileService: SearchBarMobileService,
    protected translocoService: TranslocoService,
    protected cdr: ChangeDetectorRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected guestToPassengerUIPipe: GuestToPassengerUIPipe,
    protected localStorageService: LocalStorageService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected propertiesAmplitudeService: PropertiesAmplitudeService,
    protected mobileDeviceService: MobileDeviceService,
    protected avoService: AvoService,
  ) {
    super(
      domManipulateService, appFacade, facade, searchBarMobileService, translocoService, cdr, router, route,
      guestToPassengerUIPipe, localStorageService, triggerAmplitudeService, propertiesAmplitudeService,
      mobileDeviceService, avoService
    );
  }

  get showFlightControls$(): Observable<boolean> {
    return this.facade.packageStep$.pipe(map(packageStep => this.staticShowControls &&
      ((!this.isResultsPage && !this.showHotelDetail) || (this.isResultsPage && packageStep === PackageStep.FLIGHT_RESULTS))));
  }

  get params$(): Observable<Params> {
    return this.route.params;
  }

  get activeFlightClass$(): Observable<FlightClass> {
    return this.flightClass$.pipe(map(flightClass => !flightClass ? FlightClass.CHEAPER : flightClass));
  }

  get visibleSelectClass(): boolean {
    return ENV.countryModules.show_select_class;
  }

  public isActiveFlightType$(type: FlightType): Observable<boolean> {
    return this.flightType$.pipe(map(flightType => flightType === type));
  }

  public swapPlaces(): void {
    this.facade.swapPlaces(this.searchType, true);
  }

  public togglePackagesOption(option?: string): void {
    this.showFlightOptionSelection = !this.showFlightOptionSelection;
    if (option) this.activeOption = option;
  }

  public showMobileCalendar(type: CalendarField): void {
    this.showMobileCalendarEvent.emit(type);
  }

  public openHotelRooms(): void {
    if (this.hotelRoomsSelectorRef) this.hotelRoomsSelectorRef.externalOpenRooms();
    this.cdr.detectChanges(); this.cdr.markForCheck();
  }

  public searchPackages(): void {
    this.searchPackagesTrigger$.pipe(
      take(1),
      switchMap(() => this.triggerEventsAndToggleSearchBar$())
    ).subscribe().unsubscribe();
  }

  public toggleSearchBar(): void {
    this.triggerEventsAndToggleSearchBar$().pipe(
      switchMap(() => zip(
        this.isEditButtonVisible$, this.editSearchIsActivated$
      ).pipe(
        take(1),
        tap(([isEditButtonVisible, editSearchIsActivated]) => {
          if (isEditButtonVisible) {
            const current = editSearchIsActivated ? HeaderProductType.Packages : null;
            this.facade.setEditSearchByProduct(current, HeaderProductType.Packages);
          }
        })
      ))
    ).subscribe().unsubscribe();
  }

  private setSearchParamsUI(editSearchIsActivated: boolean): void {
    if (editSearchIsActivated) {
      this.domManipulateService.setBodyScroll(false);
    } else if (this.domManipulateService.isValidManipulateByPreviousOverflowY) {
      this.domManipulateService.setBodyScroll(true);
    }
  }

  private triggerEventsAndToggleSearchBar$(): Observable<void> {
    return this.editSearchIsActivated$.pipe(
      take(1),
      tap(editSearchIsActivated => {
        this.setSearchParamsUI(editSearchIsActivated);
      }),
      map(() => null)
    );
  }

}
